import moment from 'moment'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { CHARTER_FLIGHT_DETAILS } from 'constants/charterFlight'
import useSearchFlights from '../useSearchFlights'
import { fareCabinLabelMap, FlightsClassTypes } from 'constants/flight'
import { CHARTER } from 'constants/provider'
import { EmptyArray } from 'lib/array/arrayUtils'
import { useContext, useMemo } from 'react'
import { FareFamiliesResponse, getFlightFareFamilies } from 'api/flights'
import GeoContext from 'contexts/geoContext'
import { mergeOccupants } from 'lib/offer/occupancyUtils'
import { useQuery } from '@tanstack/react-query'
import { EmptyObject } from 'lib/object/objectUtils'
import { getDayOfWeekAtLeastOneWeekAway } from 'lib/flights/charterFlightUtils'
import { useAppSelector } from 'hooks/reduxHooks'

export function extractDepartingAndReturningFaresForClass(fareFamilies: FareFamiliesResponse, fareClass: FlightsClassTypes): [App.FlightFareFamily | undefined, App.FlightFareFamily | undefined] {
  const fareCabinLabel = fareCabinLabelMap[fareClass]
  const departingFareFamily = fareFamilies.upsellFaresPerLeg?.[0]?.find(upsell => upsell.cabin === fareCabinLabel)
  const returningFareFamily = fareFamilies.upsellFaresPerLeg?.[1]?.find(upsell => upsell.cabin === fareCabinLabel)
  return [departingFareFamily, returningFareFamily]
}

interface DateRange {
  departureDate: string
  returnDate: string
  checkIn: string
  checkOut: string
  durationDays: number // Total days from departureDate to returnDate
  nights: number // Nights from checkIn to checkOut
}

export default function useCharterFlightJourneys({
  charterBundleRoute,
  departDate,
  returnDate,
  occupancies = EmptyArray,
  fareClass,
  disabled,
}: {
  charterBundleRoute?: App.CharterBundleRoute
  departDate?: string
  returnDate?: string
  occupancies: Array<App.Occupants>
  fareClass: FlightsClassTypes
  disabled?: boolean
}) {
  const routeInfo = charterBundleRoute ? CHARTER_FLIGHT_DETAILS[charterBundleRoute] : undefined
  const { currentRegionCode } = useContext(GeoContext)

  const defaultDepartureDate = routeInfo ?
      getDayOfWeekAtLeastOneWeekAway(routeInfo.departureDays[0]) :
      moment() // Search will be disabled when there's no route info, so this fallback won't be used
  const defaultDuration = routeInfo?.validDurations[0] ?? 5

  // GET FLIGHTS
  const flightSearchArgs: Parameters<typeof useSearchFlights>[0] = {
    itinerary: [{
      departureAirport: routeInfo?.originAirport.airportCode ?? '',
      arrivalAirport: routeInfo?.destinationAirport.airportCode ?? '',
      departureDate: departDate ?? defaultDepartureDate.format(ISO_DATE_FORMAT),
    },
    {
      departureAirport: routeInfo?.destinationAirport.airportCode ?? '',
      arrivalAirport: routeInfo?.originAirport.airportCode ?? '',
      departureDate: returnDate ?? defaultDepartureDate.add(defaultDuration, 'day').format(ISO_DATE_FORMAT),
    }],
    occupancies,
    fareClass,
    source: CHARTER,
  }

  const {
    journeys: departingJourneys,
    fetching: fetchingJourneys,
    error: journeysError,
    searchResultsTimestamp,
    providerSearchId,
    key: flightSearchKey,
  } = useSearchFlights(flightSearchArgs, {
    disabled,
    activeSegmentKey: 'departing',
    segments: ['departing', 'returning'],
  })

  const departingJourney = departingJourneys.at(0)

  // Get raw flight data from Redux to extract all available dates
  const flightsV2 = useAppSelector(state => state.flights.searchV2Flights[flightSearchKey])

  // Extract dateRangesByMonth from all departing and returning flights
  const dateRangesByMonth = useMemo<Record<string, Array<DateRange>>>(() => {
    if (!flightsV2?.fares?.[0] || !Array.isArray(flightsV2.fares[0])) {
      return {}
    }

    const departingFares = flightsV2.fares[0]
    const returningFares = flightsV2.fares[1] || []

    // Create a map of returning fares by their ID for quick lookup
    const returningFaresById = new Map<string, App.JourneyV2>()
    returningFares.forEach(fare => {
      returningFaresById.set(fare.id, fare)
    })

    const dateRangesMap = new Map<string, DateRange>()

    // Extract dates from flight pairs
    departingFares.forEach(departingFare => {
      const departingFlights = departingFare.flightGroup?.flights
      if (!departingFlights || departingFlights.length === 0) return

      const firstDepartingFlight = departingFlights[0]
      const lastDepartingFlight = departingFlights[departingFlights.length - 1]

      if (!firstDepartingFlight?.departingDate || !lastDepartingFlight?.arrivalDate) return

      const departureDate = moment(firstDepartingFlight.departingDate).format(ISO_DATE_FORMAT)
      const checkIn = moment(lastDepartingFlight.arrivalDate).format(ISO_DATE_FORMAT)

      // Find ALL corresponding returning flights using pairedFareId (may have multiple durations)
      if (departingFare.pairedFareId && departingFare.pairedFareId.length > 0) {
        // Process all paired return fares (e.g., 5 nights and 12 nights options)
        departingFare.pairedFareId.forEach(pairedId => {
          const returningFare = returningFaresById.get(pairedId)
          if (!returningFare) return

          const returningFlights = returningFare.flightGroup?.flights
          if (!returningFlights || returningFlights.length === 0) return

          const firstReturningFlight = returningFlights[0]
          const lastReturningFlight = returningFlights[returningFlights.length - 1]

          const returnDate = lastReturningFlight?.arrivalDate
            ? moment(lastReturningFlight.arrivalDate).format(ISO_DATE_FORMAT)
            : undefined
          const checkOut = firstReturningFlight?.departingDate
            ? moment(firstReturningFlight.departingDate).format(ISO_DATE_FORMAT)
            : undefined

          // Only add if we have all required dates
          if (departureDate && checkIn && returnDate && checkOut) {
            // Calculate durationDays (departureDate -> returnDate)
            const durationDays = moment(returnDate).diff(moment(departureDate), 'days')
            // Calculate nights (checkIn -> checkOut)
            const nights = moment(checkOut).diff(moment(checkIn), 'days')

            // Use departureDate + returnDate as key to support multiple durations for same departure date
            const uniqueKey = `${departureDate}-${returnDate}`
            if (!dateRangesMap.has(uniqueKey)) {
              dateRangesMap.set(uniqueKey, {
                departureDate,
                returnDate,
                checkIn,
                checkOut,
                durationDays,
                nights,
              })
            }
          }
        })
      }
    })

    // Group by month
    const groupedByMonth: Record<string, Array<DateRange>> = {}
    dateRangesMap.forEach((dateRange) => {
      const monthKey = moment(dateRange.departureDate).format('MMM')
      if (!groupedByMonth[monthKey]) {
        groupedByMonth[monthKey] = []
      }
      groupedByMonth[monthKey].push(dateRange)
    })

    // Sort dates within each month
    Object.keys(groupedByMonth).forEach(monthKey => {
      groupedByMonth[monthKey].sort((a, b) => {
        // First sort by departureDate
        const departureDiff = moment(a.departureDate).valueOf() - moment(b.departureDate).valueOf()
        if (departureDiff !== 0) return departureDiff
        // If same departure date, sort by returnDate (shorter duration first)
        return moment(a.returnDate).valueOf() - moment(b.returnDate).valueOf()
      })
    })

    return groupedByMonth
  }, [flightsV2?.fares])

  const {
    journeys: returningJourneys,
  } = useSearchFlights(flightSearchArgs, {
    // Always disable for the return flights, because it would just do the same search again.
    // It will still return the relevant data (filtered for returning flights) that was fetched
    // by the previous hook call.
    disabled: true,
    activeSegmentKey: 'returning',
    segments: ['departing', 'returning'],
    selectedFlights: departingJourney ? { departing: departingJourney } : undefined,
  })

  // Returning journey is the last journey in the returningJourneys array
  const returningJourney = returningJourneys.at(-1)

  // GET FARE FAMILIES
  const {
    data: { fareFamilies, departingFareFamily, returningFareFamily } = EmptyObject as { fareFamilies?: FareFamiliesResponse, departingFareFamily?: App.FlightFareFamily | undefined, returningFareFamily?: App.FlightFareFamily | undefined },
    status: fareFamiliesFetchStatus,
  } = useQuery<{ fareFamilies?: FareFamiliesResponse, departingFareFamily?: App.FlightFareFamily | undefined, returningFareFamily?: App.FlightFareFamily | undefined }>({
    queryFn: async() => {
      if (!providerSearchId || !departingJourney || !returningJourney) {
        return EmptyObject
      }

      const fareFamilies = await getFlightFareFamilies({
        searchId: providerSearchId,
        sessionId: '',
        origin: departingJourney.flightGroup.flights[0].departureAirport,
        destination: returningJourney.flightGroup.flights[0].departureAirport,
        occupancies: mergeOccupants(occupancies),
        region: currentRegionCode,
        isStandalone: false,
        selectedFareIds: [departingJourney.id, returningJourney.id],
      })

      const [departingFareFamily, returningFareFamily] = extractDepartingAndReturningFaresForClass(fareFamilies, fareClass)

      return { fareFamilies, departingFareFamily, returningFareFamily }
    },
    queryKey: [departingJourney?.flightGroup.flights[0].departureAirport, returningJourney?.flightGroup.flights[0].departureAirport, departingJourney?.id, returningJourney?.id, occupancies, currentRegionCode],
    enabled: !!(providerSearchId && departingJourney && returningJourney),
  })

  // If journey search is complete but no journeys found, we should not be fetching
  // However, if we have route and dates but no data yet, we should show loading (especially for SSR)
  const hasSearchCompleted = !!searchResultsTimestamp || !!journeysError
  const shouldBeFetching = !!charterBundleRoute && (
    fetchingJourneys ||
    (fareFamiliesFetchStatus === 'loading' && !!departingJourney && !!returningJourney) ||
    (!hasSearchCompleted && !departingJourney && !returningJourney && (!!departDate || !!returnDate))
  )

  return {
    departingJourney,
    returningJourney,
    fetching: shouldBeFetching,
    error: !!charterBundleRoute && (!!journeysError || fareFamiliesFetchStatus === 'error'),
    searchResultsTimestamp,
    providerSearchId,
    departingFareFamily,
    returningFareFamily,
    fareFamilies,
    dateRangesByMonth,
  }
}
